/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Grounded in docs/agent-context/subsystems/operations.md and
 * mobile.md Marketing Constraints.
 *
 * IMPORTANT framing:
 * - There is NO native mobile app. "Field Ops" means mobile-responsive
 *   web UI. Do NOT imply iOS/Android app availability.
 * - Clock-in/out with geolocation is REAL. Geofence is OPTIONAL,
 *   not a hard block — do not promise geofence enforcement.
 * - Self-approval is blocked at the API (real compliance win).
 * - Field view lives at /app/field/, not in components/features/field.
 * - project_task_management feature flag gates the work orders system —
 *   verify default before promising always-available.
 * - Field Ops UI is NEW, needs real-user testing and polish.
 *   Do NOT overclaim mobile stability.
 * - Budget actuals engine is NOT YET BUILT — don't promise budget
 *   actuals features.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sunrise,
  Clock,
  MapPin,
  ShieldCheck,
  Users,
  CheckCircle2,
  FileDown,
  Layers,
  Smartphone,
  Briefcase,
  DollarSign,
  ClipboardList,
  UserCheck,
  Hammer,
  Calendar as CalendarIcon,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "Field Ops & Time Tracking",
  title: "Field operations,",
  titleHighlight: "built for the people on the jobsite.",
  description:
    "Give field teams a simple mobile web workflow for clocking in, seeing assignments, and logging clean time while managers keep approval and labor-cost control.",

  featureGrid: {
    title: "Field ops without the field-ops tax",
    items: [
      {
        icon: Clock,
        title: "Clock In & Out",
        description:
          "A simple clock button on a mobile-responsive web page. Field workers open the URL on their phone, clock in, do the job, clock out. No app install.",
      },
      {
        icon: MapPin,
        title: "Optional Geolocation",
        description:
          "Capture location context on clock events when needed, without forcing hard geofence behavior.",
      },
      {
        icon: ShieldCheck,
        title: "Self-Approval Blocked",
        description:
          "Prevent self-approval so time review stays accountable across crews and supervisors.",
      },
      {
        icon: Sunrise,
        title: "My Day View",
        description:
          "Field workers open My Day and see their tasks for today in route-optimized order. Tap a task to see details, linked documents, and the address. Clock in from the same view.",
      },
      {
        icon: Users,
        title: "Crew Management",
        description:
          "Dedicated Crews page for assembling and managing crews. Assign work to a crew instead of picking individuals job by job.",
      },
      {
        icon: CheckCircle2,
        title: "Bulk Time Approval",
        description:
          "Approve a whole week's worth of time entries in one pass. Rejections remove related pending costs cleanly, and approvals post labor costs automatically.",
      },
      {
        icon: UserCheck,
        title: "Worker Profile Rate Auto-Fill",
        description:
          "Each worker has a profile with hourly rate, overtime rate, and labor burden. Time entries auto-fill from the profile so nobody's manually typing a rate per shift.",
      },
      {
        icon: FileDown,
        title: "Time Entry Export",
        description:
          "Export approved time entries by date range, crew, or project. Feeds external payroll, bookkeeping, or reporting systems when you need it.",
      },
      {
        icon: Layers,
        title: "Approved Hours → Project Actuals",
        description:
          "When a manager approves a time entry, labor cost updates post automatically with the right worker, task, and rate context.",
      },
    ],
  },

  workflow: {
    title: "What a field worker's day actually looks like",
    description:
      "A practical day flow from assignment to approved labor cost, built for real field conditions.",
    steps: [
      {
        label: "Start the Day",
        title: "Open My Day on your phone",
        description:
          "Field workers start from a phone-friendly daily assignment view with the tasks and context needed to begin work.",
        details: [
          "Mobile-responsive web UI — no native app",
          "Works on any phone browser",
          "Route-ordered tasks for the day",
          "Linked documents accessible inline",
          "Role-scoped — only assigned work is visible",
        ],
      },
      {
        label: "Clock In",
        title: "One tap. Optional GPS capture.",
        description:
          "Start time quickly from the assigned task, with optional location context available for verification workflows.",
        details: [
          "Single-tap clock-in",
          "Optional GPS capture per clock event",
          "Task-scoped time tracking",
          "Rate auto-filled from worker profile",
          "No geofence rejection — just honest capture",
        ],
      },
      {
        label: "Work the Day",
        title: "Handle the work, not the app",
        description:
          "Keep field workflows lightweight so crews spend time on the job, not navigating software.",
        details: [
          "Minimal UI noise during the workday",
          "Task completion inline",
          "Document access per task",
          "No spam notifications from the tool",
        ],
      },
      {
        label: "Clock Out",
        title: "One tap ends the day",
        description:
          "Submit time entries into the correct approval queue at the end of work with minimal friction.",
        details: [
          "Single-tap clock-out",
          "Time entry submitted for approval automatically",
          "Self-approval blocked through role-aware approval checks",
          "Entry routes to the right approver based on role",
        ],
      },
      {
        label: "Approve & Post",
        title: "Manager approves, cost lands on the project",
        description:
          "Managers review and approve entries so labor costs flow into project financials cleanly and consistently.",
        details: [
          "Bulk or individual approval workflow",
          "Rejections remove related pending costs cleanly",
          "Approved entries create project cost records automatically",
          "Labor rate, worker, task all tagged",
          "Project labor-cost visibility updates immediately",
        ],
      },
    ],
  },

  capabilities: [
    {
      icon: Smartphone,
      title: "Mobile-Responsive Web, Not A Native App",
      description:
        "Use a phone browser workflow that avoids app-store friction and keeps field adoption simple.",
      bullets: [
        "Zero install, zero update, zero friction",
        "Works on any phone browser — iOS, Android, whatever",
        "Same login as the web app",
        "Dedicated field view with My Day scheduling access",
        "Clock button component optimized for thumb use",
      ],
    },
    {
      icon: ShieldCheck,
      title: "Self-Approval Blocked — Not Just A UI Rule",
      description:
        "Protect time integrity with approval-chain enforcement that prevents users from approving their own entries.",
      bullets: [
        "Self-approval is rejected by approval-rule enforcement",
        "Role-based approval chain (Crew Leader → Laborer, Manager → Crew Leader)",
        "Full audit log per approval action",
        "Rejections remove related pending costs cleanly with reason",
        "Compliance win built into the data model",
      ],
    },
    {
      icon: Layers,
      title: "Approved Hours Flow Into Project Actuals Automatically",
      description:
        "Move from approved time to project labor-cost visibility without extra reconciliation work.",
      bullets: [
        "Approval posts labor cost entries in one step",
        "Worker, task, rate, and cost type auto-tagged",
        "Rate inherited from worker profile",
        "Labor actuals are visible for project cost review",
        "Teams can react faster to labor-cost drift",
      ],
    },
    {
      icon: MapPin,
      title: "Optional Geolocation — Honest Tracking, Not Surveillance",
      description:
        "Use location capture as an optional verification signal, not as all-day monitoring or hard geofence enforcement.",
      bullets: [
        "GPS capture on clock-in and clock-out events",
        "Optional per org — disable if it doesn't fit your culture",
        "NOT a geofence — no hard rejection of clock events",
        "Manager-side visibility for verification",
        "No all-day location tracking",
      ],
    },
  ],

  connections: {
    title: "How field ops connects to the rest of CC",
    items: [
      {
        icon: CalendarIcon,
        flow: "Schedule → My Day",
        description:
          "Dispatch board assignments flow into the field worker's My Day view. Route order, tasks, linked documents — all in one place sized for a phone.",
      },
      {
        icon: Briefcase,
        flow: "Approved Time → Project Actuals",
        description:
          "Manager-approved time entries create project cost records automatically so teams can track labor outcomes with less manual reconciliation.",
      },
      {
        icon: DollarSign,
        flow: "Actual Costs → Budget Overlay",
        description:
          "Approved labor actuals are captured for project-level financial visibility and downstream reporting workflows.",
      },
      {
        icon: Hammer,
        flow: "Work Orders → Project Tasks → Time Entries",
        description:
          "Project tasks (work orders) created from WON estimates become the unit of time tracking. Every time entry is scoped to a specific task.",
      },
    ],
  },

  relatedFeatures: [
    {
      label: "Schedule & Dispatch",
      href: "/features/schedule",
      icon: CalendarIcon,
    },
    {
      label: "Project Management",
      href: "/features/project-management",
      icon: Briefcase,
    },
    {
      label: "Budgets & Invoices",
      href: "/features/budgets-invoices",
      icon: DollarSign,
    },
    {
      label: "Team & Permissions",
      href: "/features/collaboration",
      icon: Users,
    },
    {
      label: "Documents",
      href: "/features/documents",
      icon: ClipboardList,
    },
    {
      label: "Change Orders",
      href: "/features/change-orders",
      icon: ShieldCheck,
    },
  ],
};

export function FieldOpsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
